import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, BadgeCheck, Mail } from 'lucide-react';
import { useIsMobile } from '../ui';
import LogoImage from '../../assets/HopeForSomeone.png';

const TRUST_ITEMS = [
  { Icon: ShieldCheck, text: 'Your data is encrypted & private' },
  { Icon: Clock,        text: '24/7 urgent request alerts' },
  { Icon: BadgeCheck,   text: 'Admin-verified organizations' },
];

const COLUMNS = [
  {
    title: 'Platform',
    links: [
      ['How it works', '/how-it-works'],
      ['Browse requests', '/requests'],
      ['Donation camps', '/camps'],
      ['Post a request', '/requests/new'],
    ],
  },
  {
    title: 'Get started',
    links: [
      ['Become a donor', '/register?role=donor'],
      ['Register your organization', '/register?role=organization'],
      ['Sign in', '/login'],
    ],
  },
  {
    title: 'Legal',
    links: [
      ['Privacy Policy', '/privacy'],
      ['Terms of Service', '/terms'],
    ],
  },
];

export default function Footer() {
  const isMobile = useIsMobile();
  const year = new Date().getFullYear();

  return (
    <footer style={{
      marginTop: isMobile ? '32px' : '56px',
      borderTop: '1px solid var(--gray-200)',
      background: 'var(--white)',
    }}>
      <div style={{
        maxWidth: '1200px', margin: '0 auto',
        padding: isMobile ? '32px 16px 20px' : '48px 24px 28px',
      }}>
        {/* Trust strip */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? '14px' : '12px',
          padding: isMobile ? '18px' : '20px 24px',
          background: 'var(--red-50)',
          border: '1px solid var(--red-mid)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: isMobile ? '28px' : '40px',
        }}>
          {TRUST_ITEMS.map(({ Icon, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '9px', flexShrink: 0,
                background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: 'var(--shadow-xs)',
              }}>
                <Icon size={16} color="var(--red-dark)" strokeWidth={2.2} />
              </div>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gray-800)', lineHeight: 1.3 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Brand + link columns */}
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', gap: isMobile ? '28px' : '40px',
          marginBottom: '28px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <Link to="/" style={{ display: 'inline-block', textDecoration: 'none', marginBottom: '10px' }}>
              <img src={LogoImage} alt="HopeForSomeone" style={{ height: '200px', width: 'auto', display: 'block' }} />
            </Link>
            <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', lineHeight: 1.6 }}>
              Connecting blood donors, recipients, and organizations — so no request goes unanswered.
            </p>
          </div>

          <div style={{ display: 'flex', gap: isMobile ? '32px' : '56px', flexWrap: 'wrap' }}>
            {COLUMNS.map(col => (
              <div key={col.title}>
                <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
                  {col.title}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {col.links.map(([label, to]) => (
                    <Link key={label} to={to} style={{ fontSize: '13px', color: 'var(--gray-600)', fontWeight: 500 }}>
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
                Contact
              </div>
              <a href="mailto:hello@hopeforsomeone.com" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--gray-600)', fontWeight: 500 }}>
                <Mail size={13} />
                hello@hopeforsomeone.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{
          paddingTop: '20px', borderTop: '1px solid var(--gray-100)',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
            © {year} HopeForSomeone. All rights reserved.
          </span>
          <span style={{ fontSize: '11.5px', color: 'var(--gray-400)', maxWidth: '420px', lineHeight: 1.5 }}>
            In a medical emergency, always contact your local emergency services first.
          </span>
        </div>
      </div>
    </footer>
  );
}
